import React, { useCallback, useEffect, useMemo, useState } from "react";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "services/api";

const CATEGORY_ORDER = ["clients", "linguists", "services", "invoices", "expenses"];
const CATEGORY_LABELS = {
  clients: "Clients",
  linguists: "Linguists",
  services: "Services",
  invoices: "Invoices",
  expenses: "Expenses",
};

const getQueryTerms = (value) => {
  const rawTerms = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  const seen = new Set();
  return rawTerms.filter((term) => {
    const key = term.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getItemMatchedTerms = (item, normalizedTerms) => {
  const itemTokens = Array.isArray(item?.matchedTokens)
    ? item.matchedTokens.map((token) => String(token || "").toLowerCase())
    : [];

  if (!itemTokens.length) return [];
  return normalizedTerms.filter((term) => itemTokens.includes(term));
};

const buildItemGroups = (items, queryValue, queryTerms) => {
  const normalizedTerms = queryTerms.map((term) => term.toLowerCase());

  if (normalizedTerms.length < 2) {
    return {
      groups: [
        {
          key: "all-items",
          title: `Results for "${queryValue}"`,
          items,
        },
      ],
      topTier: items.length ? 0 : 2,
    };
  }

  const grouped = [];
  const remaining = [...items];

  const allTermMatches = remaining.filter((item) => {
    const matchedTerms = getItemMatchedTerms(item, normalizedTerms);
    return normalizedTerms.every((term) => matchedTerms.includes(term));
  });

  if (allTermMatches.length) {
    grouped.push({
      key: "all-terms",
      title: `Results for "${queryValue}"`,
      items: allTermMatches,
    });
  }

  const allTermIds = new Set(allTermMatches.map((item) => `${item.entityType}-${item.id}`));
  let unmatchedAfterAll = remaining.filter((item) => !allTermIds.has(`${item.entityType}-${item.id}`));

  queryTerms.forEach((term, index) => {
    const normalizedTerm = normalizedTerms[index];
    const termItems = unmatchedAfterAll.filter((item) =>
      getItemMatchedTerms(item, [normalizedTerm]).includes(normalizedTerm),
    );

    if (termItems.length) {
      grouped.push({
        key: `term-${normalizedTerm}`,
        title: `Results for "${term}"`,
        items: termItems,
      });

      const termItemIds = new Set(termItems.map((item) => `${item.entityType}-${item.id}`));
      unmatchedAfterAll = unmatchedAfterAll.filter(
        (item) => !termItemIds.has(`${item.entityType}-${item.id}`),
      );
    }
  });

  const groupedIds = new Set(
    grouped.flatMap((group) => group.items.map((item) => `${item.entityType}-${item.id}`)),
  );
  const remainingItems = items.filter((item) => !groupedIds.has(`${item.entityType}-${item.id}`));

  if (remainingItems.length) {
    grouped.push({
      key: "other-matches",
      title: "Other matches",
      items: remainingItems,
    });
  }

  return {
    groups: grouped,
    topTier: allTermMatches.length ? 0 : grouped.length ? 1 : 2,
  };
};

const GlobalSearch = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { overlayData, setOverlayData, setOverlayName } = useStateContext();

  const query = String(overlayData?.query || "").trim();
  const category = String(overlayData?.category || "").trim().toLowerCase();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [isSearchHovered, setIsSearchHovered] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const isAdmin = String(currentUser?.Role || "").toLowerCase() === "admin";
  const isSearchExpanded = isMobile || isSearchHovered || isSearchFocused || Boolean(query);

  const visibleCategories = useMemo(() => {
    const base = isAdmin
      ? CATEGORY_ORDER
      : CATEGORY_ORDER.filter((key) => key !== "invoices" && key !== "expenses");

    if (category && base.includes(category)) {
      return [category];
    }

    return base;
  }, [category, isAdmin]);

  const queryTerms = useMemo(() => getQueryTerms(query), [query]);

  const groupedByCategory = useMemo(() => {
    const output = {};

    visibleCategories.forEach((categoryKey) => {
      const categoryData = data?.categories?.[categoryKey] || { items: [], total: 0 };
      const items = Array.isArray(categoryData.items) ? categoryData.items : [];
      output[categoryKey] = {
        ...categoryData,
        ...buildItemGroups(items, query, queryTerms),
      };
    });

    return output;
  }, [data, query, queryTerms, visibleCategories]);

  const orderedCategories = useMemo(() => {
    return [...visibleCategories].sort((a, b) => {
      const aTier = groupedByCategory[a]?.topTier ?? 2;
      const bTier = groupedByCategory[b]?.topTier ?? 2;
      if (aTier !== bTier) return aTier - bTier;
      return CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b);
    });
  }, [groupedByCategory, visibleCategories]);

  const fetchResults = useCallback(async () => {
    if (!query) {
      setData(null);
      return;
    }

    setLoading(true);
    try {
      if (category) {
        const mergedItems = [];
        let total = 0;
        let offset = 0;
        const pageSize = 250;
        let hasMore = true;

        while (hasMore) {
          const response = await api.get("/search/global", {
            params: {
              q: query,
              category,
              limit: pageSize,
              offset,
            },
          });

          const categoryData = response?.data?.categories?.[category] || {
            items: [],
            total: 0,
            hasMore: false,
          };

          const items = Array.isArray(categoryData.items) ? categoryData.items : [];
          mergedItems.push(...items);
          total = Number(categoryData.total || mergedItems.length);
          hasMore = Boolean(categoryData.hasMore) && items.length > 0;
          offset += items.length;

          if (items.length === 0) {
            break;
          }
        }

        setData({
          query,
          categories: {
            [category]: {
              items: mergedItems,
              total,
              hasMore: false,
            },
          },
        });
      } else {
        const response = await api.get("/search/global", {
          params: {
            q: query,
            limit: 25,
            offset: 0,
          },
        });
        setData(response.data || null);
      }
    } catch (error) {
      console.error("Failed to load global search results", error);
      setData({ categories: {} });
    } finally {
      setLoading(false);
    }
  }, [query, category]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const handleOpenItem = (item) => {
    if (!item?.routePath) return;
    setOverlayName("");
    navigate(item.routePath);
  };

  const handleShowCategory = (categoryKey) => {
    setOverlayData((previous) => ({
      ...(previous || {}),
      query,
      category: categoryKey,
    }));
  };

  const handleShowAllCategories = () => {
    setOverlayData((previous) => ({
      ...(previous || {}),
      query,
      category: "",
    }));
  };

  const handleQueryChange = (event) => {
    const nextQuery = event.target.value;
    setOverlayData((previous) => ({
      ...(previous || {}),
      query: nextQuery,
    }));
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.72)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      onClick={() => setOverlayName("")}
    >
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.alt,
          width: isMobile ? "96%" : "78%",
          minWidth: isMobile ? "unset" : "720px",
          maxWidth: "1200px",
          maxHeight: isMobile ? "96%" : "86%",
          display: "flex",
          borderRadius: "1.5rem",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: isMobile ? "1rem" : "1.5rem",
          boxShadow: theme.shadows[12],
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            mb: 2,
            gap: 2,
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
            <IconButton color="primary" size="large" onClick={() => setOverlayName("") }>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" fontWeight="bold">
                Search Results
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                Query: {query || "-"}
                {category ? ` • ${CATEGORY_LABELS[category]}` : " • All categories"}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: isMobile ? "100%" : "auto",
              justifyContent: "flex-end",
            }}
          >
            {category && (
              <Button
                variant="outlined"
                onClick={handleShowAllCategories}
                sx={{ borderRadius: "12px", flexShrink: 0 }}
              >
                All categories
              </Button>
            )}

            <Box
              onMouseEnter={() => setIsSearchHovered(true)}
              onMouseLeave={() => setIsSearchHovered(false)}
              sx={{
                width: isMobile ? "100%" : isSearchExpanded ? 380 : 52,
                transition: "width 220ms ease",
                flexShrink: 0,
              }}
            >
              <TextField
                fullWidth
                size="small"
                value={query}
                onChange={handleQueryChange}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                placeholder={isSearchExpanded ? "Search clients, linguists, services, invoices, expenses" : ""}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                  },
                  "& .MuiInputBase-input": {
                    transition: "opacity 180ms ease, width 180ms ease",
                    opacity: isSearchExpanded ? 1 : 0,
                    width: isSearchExpanded ? "auto" : 0,
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", pr: isMobile ? 0 : 0.5 }}>
          {loading ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={30} />
            </Box>
          ) : !query ? (
            <Typography variant="body2" color="text.secondary">
              Enter a query in the navbar search to begin.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {orderedCategories.map((categoryKey) => {
                const categoryData = groupedByCategory?.[categoryKey] || { items: [], total: 0, groups: [] };
                const items = Array.isArray(categoryData.items) ? categoryData.items : [];
                const groups = Array.isArray(categoryData.groups) ? categoryData.groups : [];

                if (!items.length) return null;

                return (
                  <Card key={categoryKey} sx={{ p: 1.25, borderRadius: "14px" }}>
                    <Box sx={{ px: 1, py: 0.5, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                      <Typography variant="h5" fontWeight="bold">
                        {CATEGORY_LABELS[categoryKey]} ({categoryData.total || items.length})
                      </Typography>
                      {!category && Number(categoryData.total || 0) > items.length && (
                        <Button size="small" onClick={() => handleShowCategory(categoryKey)} sx={{ borderRadius: "10px" }}>
                          Show all
                        </Button>
                      )}
                    </Box>
                    <Divider sx={{ mb: 1 }} />

                    <Stack spacing={1}>
                      {groups.map((group) => (
                        <Box key={`${categoryKey}-${group.key}`}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              px: 1,
                              mb: 0.5,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              color: "text.secondary",
                            }}
                          >
                            {group.title}
                          </Typography>

                          <Stack spacing={0.5}>
                            {group.items.map((item) => (
                              <Box
                                key={`${categoryKey}-${group.key}-${item.id}`}
                                onClick={() => handleOpenItem(item)}
                                sx={{
                                  px: 1,
                                  py: 1,
                                  borderRadius: "10px",
                                  cursor: "pointer",
                                  "&:hover": {
                                    bgcolor: "action.hover",
                                  },
                                }}
                              >
                                <Typography variant="body1" fontWeight={700}>
                                  {item.name || "Result"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {item.subtitle || ""}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Card>
                );
              })}

              {orderedCategories.every((categoryKey) => {
                const items = groupedByCategory?.[categoryKey]?.items || [];
                return items.length === 0;
              }) && (
                <Typography variant="body2" color="text.secondary">
                  No results found for this query.
                </Typography>
              )}
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default GlobalSearch;
